import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  User,
  UserCredential,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import {
  type DwdClientIdResponse,
  ONBOARDING_SETUP_PATH,
  type ResolveLoginResponse,
} from '@cdw-ae/common-data';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Store } from '@ngrx/store';
import { IdTokenResult, onIdTokenChanged } from 'firebase/auth';
import {
  BehaviorSubject,
  firstValueFrom,
  from,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import * as UserIdentitySettingsActions from '../../../+state/user-identity/user-identity-settings.actions';
import { UserClaims, UserData } from '../../../+state/user-identity/user-identity-settings.models';
import { RootState } from '../../interfaces/rootState.interface';
import { clearGridSessions, clearPersistedGridSort } from '../../utils/session-storage.utils';
import { ApiUrlBuilderService } from '../api-url-builder.service';
import { LoginVerdictStateService } from './login-verdict-state.service';
@Injectable({
  providedIn: 'root',
})
export class UserIdentityService {
  private initialAuthCheckComplete = new BehaviorSubject<boolean>(false);
  private isValidatingClaims = false; // Guard against re-entry from token refresh
  /**
   * Firebase's `onIdTokenChanged` fires on genuine sign-in AND on its own
   * silent background token refresh (~hourly) of an already-signed-in user —
   * that's the whole reason it exists as distinct from `onAuthStateChanged`.
   * Tracking the last-seen uid lets us tell those apart, so grid/component
   * state only clears on an actual login, not every refresh mid-session.
   */
  private lastAuthenticatedUid: string | null = null;
  constructor(
    private appState: Store<RootState>,
    private auth: Auth,
    private http: HttpClient,
    private readonly urlBuilder: ApiUrlBuilderService,
    private readonly snackbarService: SnackbarService,
    private readonly router: Router,
    private readonly loginVerdictState: LoginVerdictStateService
  ) {
    onIdTokenChanged(this.auth, async (user) => {
      if (user) {
        // Guard prevents infinite loop AND ensures single setUser dispatch per login cycle
        // getIdTokenResult(true) triggers onIdTokenChanged, but guard blocks re-entry
        if (!this.isValidatingClaims) {
          this.isValidatingClaims = true;
          try {
            // Get initial token for auth header (don't dispatch yet)
            const idTokenResult = (await user.getIdTokenResult()) as IdTokenResult & {
              claims: UserClaims;
            };

            // Login router (EDUS-2726): resolve the verdict FIRST — the
            // backend mints claims per verdict, the client routes on it.
            const resolution: ResolveLoginResponse = await firstValueFrom(
              this.resolveLogin(idTokenResult.token)
            );

            // Only a genuine sign-in should wipe grid session / sort state — a
            // background token refresh of the same user must not (see
            // lastAuthenticatedUid doc comment above). Verdict-independent:
            // a different uid means stale grid state either way.
            if (this.lastAuthenticatedUid !== user.uid) {
              clearGridSessions();
              clearPersistedGridSort();
            }
            this.lastAuthenticatedUid = user.uid;

            // routeByVerdict owns the forced token refresh, the single
            // updateUserData dispatch and ensureRecurringSync (APP verdict).
            await this.routeByVerdict(resolution, user);
          } catch (error) {
            const errorMessage = this.extractUserFriendlyError(error);

            // Dispatch failure action for effects to handle navigation
            this.appState.dispatch(
              UserIdentitySettingsActions.tokenValidationFailed({ error: errorMessage })
            );

            // Clear Firebase auth state
            await this.auth.signOut();

            // Clear NgRx state
            this.appState.dispatch(
              UserIdentitySettingsActions.setUser({ userData: null })
            );
          } finally {
            this.isValidatingClaims = false;
          }
        }
      } else {
        // Signed out — the next sign-in (even by the same uid) must count
        // as a genuine login again, not a refresh.
        this.lastAuthenticatedUid = null;
        this.appState.dispatch(
          UserIdentitySettingsActions.setUser({ userData: null })
        );
      }

      if (!this.initialAuthCheckComplete.value) {
        this.initialAuthCheckComplete.next(true);
      }
    });
  }

  /**
   * Route on the login verdict. Assumes the isValidatingClaims guard is held by
   * the caller — the APP/WIZARD token refreshes below re-fire onIdTokenChanged,
   * which the guard must suppress.
   */
  private async routeByVerdict(
    resolution: ResolveLoginResponse,
    user: User
  ): Promise<void> {
    switch (resolution.verdict) {
      case 'APP': {
        // Force refresh to get updated custom claims from Firebase
        const idTokenResult = (await user.getIdTokenResult(true)) as IdTokenResult & {
          claims: UserClaims;
        };
        // The other two branches already record the resolution; APP was the
        // odd one out. The app footer reads context.expiry from here
        // (EDUS-2810) — it re-populates on every load because resolve-login
        // runs on each onIdTokenChanged, so a hard refresh costs one round
        // trip during which the footer line stays hidden.
        //
        // MUST come before the setUser dispatch below (EDUS-2852): that
        // dispatch starts the chain that decides where the user lands
        // (triggerPostLogin$ → postLogin → post-auth hooks →
        // resolvePostAuthTarget), and resolvePostAuthTarget now reads
        // `firstEntry` back off this signal. Setting it afterwards left that
        // read racing an async hop that happened to be slow enough to win.
        //
        // recordResolution, not state.set: the force-refresh two lines up
        // re-fires onIdTokenChanged, so this same branch runs again with the
        // backend's (correct) `firstEntry: false` once the stamp exists. The
        // flag latches there so the second pass can't erase the first entry —
        // see the docblock on recordResolution.
        this.loginVerdictState.recordResolution(resolution);
        // Single dispatch with final claims - triggers effects exactly once
        this.updateUserData(user, idTokenResult.token, idTokenResult.claims);
        // Fire-and-forget: ensure a recurring cache refresh job exists
        this.ensureRecurringSync(idTokenResult.token);
        break;
      }
      case 'WIZARD': {
        // Refresh so wizard API calls carry the minimal onboarding claims.
        // No setUser dispatch — the wizard is not an app session.
        await user.getIdTokenResult(true);
        this.loginVerdictState.recordResolution(resolution);
        // Literal route per AC-1.1 (EDUS-2680); US-2 owns the wizard.
        await this.router.navigate([ONBOARDING_SETUP_PATH]);
        break;
      }
      default: {
        // All block verdicts land on the login-status screen. They keep the
        // Firebase session (a reload re-resolves — so a fixed license /
        // permission heals itself — and the screen offers sign-out).
        this.loginVerdictState.recordResolution(resolution);
        await this.router.navigate(['/login-status']);
      }
    }
  }

  loginWithGoogle(): Observable<UserCredential> {
    const provider = new GoogleAuthProvider();
    return from(signInWithPopup(this.auth, provider)).pipe(
      catchError((error) => {
        //  console.error('Firebase Sign-In failed', error);
        return throwError(
          () => new Error(error?.message || 'Firebase Sign-In failed')
        );
      })
    );
  }

  /**
   * The signed-in Firebase user's display profile, for chrome such as the
   * onboarding rail avatar. Returns null when no user is signed in. The wizard
   * session is deliberately NOT stored in NgRx (WIZARD verdict), so wizard
   * components read identity here rather than from the store — and keep the
   * Firebase (rxfire ESM) dependency behind this service.
   */
  public getCurrentUserProfile(): {
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
  } | null {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }
    return {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
    };
  }

  /**
   * The current Firebase ID token, or null when signed out. Firebase serves it
   * from cache until it nears expiry, so this is cheap to call repeatedly.
   *
   * Exposed for the wizard's page-unload lock release (US-12), which must build
   * its own `fetch(..., { keepalive: true })` — HttpClient (and therefore the
   * auth interceptor) can't survive the page going away. Everything else should
   * keep using HttpClient and let the interceptor attach the token.
   */
  public async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }
    try {
      return await user.getIdToken();
    } catch {
      return null;
    }
  }

  public logout(): Observable<void> {
    // console.log('UserIdentityService: Initiating logout...');
    // Clear grid sessions and grid sort on logout
    clearGridSessions();
    clearPersistedGridSort();
    this.appState.dispatch(
      UserIdentitySettingsActions.setUser({ userData: null })
    );
    return from(this.auth.signOut()).pipe(
      catchError((error) => {
        // console.error('Firebase sign-out failed:', error);
        return of(undefined);
      })
    );
  }

  async initializeAuthState(): Promise<void> {
    await firstValueFrom(
      this.initialAuthCheckComplete.pipe(
        filter((isComplete) => isComplete === true)
      )
    );
  }

  /**
   * Re-resolve the login verdict for the current user and route on it. Used
   * after a server-side change that affects routing — specifically the end of
   * the onboarding wizard (US-8), which flips `onboarding_status` to
   * `initialized`, so the same Firebase user now resolves to APP instead of
   * WIZARD and should be routed into the app.
   *
   * Guards re-entry with `isValidatingClaims` exactly like the onIdTokenChanged
   * handler: routeByVerdict force-refreshes the token (APP/WIZARD branches),
   * which re-fires onIdTokenChanged, and the guard suppresses that duplicate.
   */
  public async reresolveAndRoute(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      await this.router.navigate(['/welcome']);
      return;
    }
    if (this.isValidatingClaims) {
      return;
    }
    this.isValidatingClaims = true;
    try {
      const idTokenResult = (await user.getIdTokenResult()) as IdTokenResult & {
        claims: UserClaims;
      };
      const resolution: ResolveLoginResponse = await firstValueFrom(
        this.resolveLogin(idTokenResult.token)
      );
      await this.routeByVerdict(resolution, user);
    } finally {
      this.isValidatingClaims = false;
    }
  }

  /**
   * Gopher's DWD client id for the Google Admin Console delegation deep link.
   * Open endpoint (token only, no claims): both the onboarding wizard's Step 3
   * and the INTEGRATION_BROKEN block screen build that link, and a blocked user
   * has no wizard claims. The scopes come from the shared GOPHER_DWD_GRANT_SCOPES
   * constant, so callers assemble the link with buildDwdDelegationLink().
   */
  getDwdClientId(): Observable<DwdClientIdResponse> {
    return this.http.get<DwdClientIdResponse>(
      this.urlBuilder.backendApi('authentication', 'dwd-client-id')
    );
  }

  private resolveLogin(token: string): Observable<ResolveLoginResponse> {
    const url = this.urlBuilder.backendApi('authentication', 'resolve-login');
    // Pass token directly in headers - store is empty during initial validation
    return this.http.get<ResolveLoginResponse>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private updateUserData(
    user: User,
    token: string,
    claims?: UserClaims
  ): void {
    const { uid, email, displayName, emailVerified, photoURL } = user;
    const currentUserData: UserData = {
      token,
      uid,
      email,
      name: displayName,
      emailVerified,
      photoURL,
      claims,
    };
    this.appState.dispatch(
      UserIdentitySettingsActions.setUser({ userData: currentUserData })
    );
  }

  private ensureRecurringSync(token: string): void {
    const url = this.urlBuilder.backendApi('all-customer-assets', 'ensure-recurring-sync');
    this.http
      .get(url, { headers: { Authorization: `Bearer ${token}` } })
      .subscribe({
        error: () => {
          this.snackbarService.showError(
            'Failed to verify recurring cache refresh schedule. Please contact support if this persists.'
          );
        },
      });
  }

  // Since EDUS-2726, expected outcomes (unlicensed, non-admin, broken DWD…)
  // are VERDICTS with their own screens, not errors — this path only sees
  // genuine malfunctions, so a generic message is the honest one.
  private extractUserFriendlyError(_error: unknown): string {
    return 'Authentication failed. Please try again or contact support.';
  }
}
